import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { venuesApi } from '../../api/venues.api';
import type { Venue, VenueCategory } from '../../types';
import { Button } from '../ui/Button';
import { toast } from '../ui/Toast';
import AddressAutocomplete from '../ui/AddressAutocomplete';
import KindSelect from '../ui/KindSelect';

const CATEGORY_VALUES: VenueCategory[] = [
  'RESTAURANT', 'CAFE', 'ATTRACTION', 'VIEWPOINT', 'NEIGHBORHOOD',
  'PARK', 'BEACH', 'NATURE', 'SHOP', 'EXPERIENCE', 'TRANSPORT_HUB', 'PARKING', 'OTHER',
];

interface Props {
  tripId: string;
  existing?: Venue;
  prefill?: Partial<Venue>;
  // The trip's places, so a category the user already made up is offered again.
  tripVenues?: Venue[];
  onSaved: (v: Venue) => void;
  onCancel: () => void;
}

export default function VenueForm({ tripId, existing, prefill, tripVenues, onSaved, onCancel }: Props) {
  const { t } = useTranslation();
  const src = existing ?? prefill;
  const [submitted, setSubmitted] = useState(false);
  const err = (bad: boolean) => bad ? ' input-error' : '';
  const [form, setForm] = useState({
    name:       src?.name       ?? '',
    category:   src?.category   ?? ('ATTRACTION' as VenueCategory),
    customCategory: src?.customCategory ?? '',
    address:    src?.address    ?? '',
    placeId:    src?.placeId    ?? '',
    mapsUrl:    src?.mapsUrl    ?? '',
    bookingUrl: src?.bookingUrl ?? '',
    priceInfo:  src?.priceInfo  ?? '',
    notes:      src?.notes      ?? '',
  });
  const [loading, setLoading] = useState(false);

  function update(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const data = {
        name:     form.name,
        category: form.category,
        // ADR-0019: the server drops it for any category but OTHER anyway.
        customCategory: form.category === 'OTHER' ? (form.customCategory.trim() || null) : null,
        address:    form.address    || null,
        // Clearing the address must also drop placeId — otherwise the server's
        // enrichFromPlaceId() re-fetches and restores the address from it.
        placeId:    form.address ? (form.placeId || null) : null,
        mapsUrl:    form.mapsUrl    || null,
        bookingUrl: form.bookingUrl || null,
        priceInfo:  form.priceInfo  || null,
        notes:      form.notes      || null,
      };
      const venue = existing
        ? await venuesApi.update(tripId, existing.id, data)
        : await venuesApi.create(tripId, data);
      toast(existing ? t('venues.toast.updated') : t('venues.toast.added', { name: venue.name }), 'success');
      onSaved(venue);
    } catch {
      toast(t('venues.toast.saveFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="label">{t('venues.form.name')} *</label>
        <input
          type="text" className={'input' + err(submitted && !form.name.trim())}
          value={form.name} onChange={e => update('name', e.target.value)}
          placeholder={t('venues.form.namePlaceholder')}
        />
      </div>

      <div>
        <label className="label">{t('venues.form.category')}</label>
        <KindSelect
          ariaLabel={t('venues.form.category')}
          options={CATEGORY_VALUES.map(v => ({ value: v, label: t(`ai.category.${v}` as Parameters<typeof t>[0]) }))}
          otherValue="OTHER"
          value={form.category}
          customName={form.customCategory}
          existingNames={(tripVenues ?? []).map(v => v.customCategory ?? '')}
          onChange={(category, customCategory) => setForm(f => ({ ...f, category, customCategory }))}
          labels={{
            yours: t('venues.form.yourCategories'), create: t('venues.form.newCategory'),
            name: t('venues.form.customCategory'), namePlaceholder: t('venues.form.customCategoryPlaceholder'),
          }}
        />
      </div>

      <div>
        <label className="label">{t('venues.form.address')}</label>
        <AddressAutocomplete
          value={form.address}
          onChange={v => setForm(f => ({ ...f, address: v }))}
          onSelect={d => setForm(f => ({
            ...f,
            address: d.address || d.name,
            placeId: d.placeId,
            mapsUrl: d.mapsUrl ?? f.mapsUrl,
          }))}
          placeholder={t('venues.form.searchPlace')}
        />
      </div>

      <div>
        <label className="label">{t('venues.form.priceInfo')}</label>
        <input
          type="text" className="input"
          value={form.priceInfo} onChange={e => update('priceInfo', e.target.value)}
          placeholder={t('venues.form.pricePlaceholder')}
        />
      </div>

      <div>
        <label className="label">{t('venues.form.bookingUrl')}</label>
        <input
          type="url" className="input"
          value={form.bookingUrl} onChange={e => update('bookingUrl', e.target.value)}
          placeholder="https://..."
        />
      </div>

      <div>
        <label className="label">{t('venues.form.notes')}</label>
        <textarea
          className="input" rows={2}
          value={form.notes} onChange={e => update('notes', e.target.value)}
          placeholder={t('venues.form.notesPlaceholder')}
        />
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="cta" className="flex-1 disabled:opacity-50 disabled:pointer-events-none" loading={loading}>
          {existing ? t('venues.editPlace') : t('venues.addPlace')}
        </Button>
        <button type="button" className="btn-secondary" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </form>
  );
}
